import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import {
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { getCurrentUser } from "../../../helpers/Utils"
import { isMetaWaDesignDemo } from "../../../helpers/metaWhatsAppDesignDemo"

const authHeaders = () => ({
  "content-type": "application/json",
  Authorization: `Bearer ${
    getCurrentUser()?.token == null ? "" : getCurrentUser().token
  }`,
})

const companyBaseUrl = () =>
  `${process.env.REACT_APP_API_URL}/${localStorage.getItem("company_name")}`

const LANGUAGES = [
  { value: "en_US", label: "English (US)" },
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "en_GB", label: "English (UK)" },
]

const CATEGORIES = [
  { value: "UTILITY", label: "Utility" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Authentication" },
]

/** High-contrast pills — theme Badge often renders white-on-white */
const PILL_STYLES = {
  approved: { background: "#0ab39c", color: "#fff" },
  pending: { background: "#f7b84b", color: "#212529" },
  rejected: { background: "#f06548", color: "#fff" },
  paused: { background: "#878a99", color: "#fff" },
  disabled: { background: "#878a99", color: "#fff" },
  utility: { background: "#299cdb", color: "#fff" },
  marketing: { background: "#405189", color: "#fff" },
  authentication: { background: "#212529", color: "#fff" },
  default: { background: "#eff2f7", color: "#212529" },
}

const StatusPill = ({ children, tone = "default" }) => {
  const style = PILL_STYLES[tone] || PILL_STYLES.default
  return (
    <span
      className="badge rounded-pill"
      style={{
        backgroundColor: style.background,
        color: style.color,
        fontWeight: 600,
        fontSize: "0.75rem",
        padding: "0.35em 0.65em",
      }}
    >
      {children}
    </span>
  )
}

const statusTone = status => {
  const s = String(status || "").toUpperCase()
  if (s === "APPROVED") return "approved"
  if (s === "PENDING") return "pending"
  if (s === "REJECTED") return "rejected"
  if (s === "PAUSED") return "paused"
  if (s === "DISABLED") return "disabled"
  return "default"
}

const categoryTone = category => {
  const c = String(category || "").toUpperCase()
  if (c === "UTILITY") return "utility"
  if (c === "MARKETING") return "marketing"
  if (c === "AUTHENTICATION") return "authentication"
  return "default"
}

const detectBodyVars = text => {
  const matches = String(text || "").match(/\{\{(\d+)\}\}/g) || []
  const nums = [
    ...new Set(matches.map(m => parseInt(m.replace(/[{}]/g, ""), 10))),
  ].sort((a, b) => a - b)
  return nums
}

const emptyForm = () => ({
  name: "",
  language: "en_US",
  category: "UTILITY",
  header_format: "NONE",
  header_text: "",
  header_example_url: "",
  body_text: "",
  footer_text: "",
  body_examples: [],
  button_enabled: false,
  button_type: "QUICK_REPLY",
  button_text: "",
  button_url: "",
  button_phone: "",
})

const MetaWhatsAppTemplatesManager = () => {
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [templates, setTemplates] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  })
  const [metaInfo, setMetaInfo] = useState(null)
  const [connected, setConnected] = useState(null)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [categoryFilter, setCategoryFilter] = useState("ALL")
  const [createOpen, setCreateOpen] = useState(false)
  const [previewTpl, setPreviewTpl] = useState(null)
  const [form, setForm] = useState(emptyForm())

  const bodyVarIndexes = useMemo(
    () => detectBodyVars(form.body_text),
    [form.body_text]
  )

  const fetchTemplates = useCallback(() => {
    if (isMetaWaDesignDemo()) {
      setLoading(false)
      setConnected(true)
      setMetaInfo({
        display_phone_number: "+91 70155 50472",
        waba_id: "987654321098765",
        business_id: "123456789012345",
      })
      const demoRows = [
        {
          id: "demo-tpl-1",
          name: "order_confirmation",
          language: "en_US",
          category: "UTILITY",
          status: "APPROVED",
          body_preview:
            "Hello {{1}}, your order {{2}} has been confirmed successfully.",
          updated_at: new Date().toISOString(),
        },
        {
          id: "demo-tpl-2",
          name: "welcome_message",
          language: "en_US",
          category: "MARKETING",
          status: "PENDING",
          body_preview:
            "Hi {{1}}, welcome to our store! How can we help you today?",
          updated_at: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "demo-tpl-3",
          name: "payment_reminder",
          language: "hi",
          category: "UTILITY",
          status: "REJECTED",
          body_preview: "Namaste {{1}}, aapka payment {{2}} pending hai.",
          rejection_reason: "Template includes promotional content in Utility",
          updated_at: new Date(Date.now() - 86400000).toISOString(),
        },
      ]
      setTemplates(demoRows)
      setStats({
        total: 3,
        approved: 1,
        pending: 1,
        rejected: 1,
      })
      return
    }

    setLoading(true)
    const params = {}
    if (statusFilter && statusFilter !== "ALL") params.status = statusFilter
    if (categoryFilter && categoryFilter !== "ALL")
      params.category = categoryFilter
    if (search.trim()) params.search = search.trim()

    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/templates`, {
        headers: authHeaders(),
        params,
      })
      .then(res => {
        setLoading(false)
        if (res.data.status) {
          setTemplates(res.data.data?.templates || [])
          setStats(
            res.data.data?.stats || {
              total: 0,
              approved: 0,
              pending: 0,
              rejected: 0,
            }
          )
          setMetaInfo({
            waba_id: res.data.data?.waba_id,
            business_id: res.data.data?.business_id,
            display_phone_number: res.data.data?.display_phone_number,
          })
          setConnected(true)
        } else {
          setTemplates([])
          setConnected(false)
          toast.error(res.data.message || "Unable to load templates.", {
            position: "top-center",
            autoClose: 6000,
          })
        }
      })
      .catch(err => {
        setLoading(false)
        setTemplates([])
        if (err?.response?.status === 404) {
          setConnected(false)
        }
        toast.error(
          err?.response?.data?.message || "Failed to load templates.",
          { position: "top-center", autoClose: 7000 }
        )
      })
  }, [statusFilter, categoryFilter, search])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  // While any template is PENDING, poll so APPROVED appears without manual sync
  // (webhook updates DB instantly; this refreshes the open page).
  useEffect(() => {
    const pending = Number(stats.pending || 0)
    if (!connected || pending <= 0) return undefined

    const timer = setInterval(() => {
      fetchTemplates()
    }, 20000)

    return () => clearInterval(timer)
  }, [connected, stats.pending, fetchTemplates])

  useEffect(() => {
    // Keep body_examples length in sync with detected variables
    setForm(prev => {
      const next = [...(prev.body_examples || [])]
      while (next.length < bodyVarIndexes.length) next.push("")
      return {
        ...prev,
        body_examples: next.slice(0, Math.max(bodyVarIndexes.length, 0)),
      }
    })
  }, [bodyVarIndexes.length])

  const syncTemplates = () => {
    setSyncing(true)
    axios
      .post(
        `${companyBaseUrl()}/whatsapp/meta/sync-templates`,
        {},
        { headers: authHeaders() }
      )
      .then(res => {
        setSyncing(false)
        if (res.data.status) {
          const d = res.data.data || {}
          toast.success(
            res.data.message ||
              `Synced ${d.upserted ?? 0} templates from Meta.`,
            { position: "top-center", autoClose: 5000 }
          )
          fetchTemplates()
        } else {
          toast.error(res.data.message || "Sync failed.", {
            position: "top-center",
            autoClose: 8000,
          })
        }
      })
      .catch(err => {
        setSyncing(false)
        toast.error(err?.response?.data?.message || "Sync failed.", {
          position: "top-center",
          autoClose: 9000,
        })
      })
  }

  const openCreate = () => {
    setForm(emptyForm())
    setCreateOpen(true)
  }

  const submitCreate = () => {
    const name = String(form.name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")

    if (!/^[a-z0-9_]+$/.test(name)) {
      toast.error(
        "Template name: only lowercase letters, numbers and underscores.",
        { position: "top-center", autoClose: 5000 }
      )
      return
    }
    if (!form.body_text.trim()) {
      toast.error("Body text is required.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (bodyVarIndexes.length > 0) {
      const missing = bodyVarIndexes.some(
        (_, i) => !String(form.body_examples[i] || "").trim()
      )
      if (missing) {
        toast.error("Provide a sample value for each body variable.", {
          position: "top-center",
          autoClose: 5000,
        })
        return
      }
    }

    const payload = {
      name,
      language: form.language,
      category: form.category,
      body_text: form.body_text.trim(),
      footer_text: form.footer_text.trim() || undefined,
      body_examples:
        bodyVarIndexes.length > 0
          ? form.body_examples.slice(0, bodyVarIndexes.length)
          : undefined,
    }

    const headerFormat = form.header_format || "NONE"
    if (headerFormat === "TEXT") {
      payload.header_format = "TEXT"
      payload.header_text = form.header_text.trim() || undefined
    } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)) {
      if (!form.header_example_url.trim()) {
        toast.error("Provide a public HTTPS sample file URL for the media header.", {
          position: "top-center",
          autoClose: 5000,
        })
        return
      }
      payload.header_format = headerFormat
      payload.header_example_url = form.header_example_url.trim()
    }

    if (form.button_enabled && form.button_text.trim()) {
      const btn = {
        type: form.button_type,
        text: form.button_text.trim(),
      }
      if (form.button_type === "URL") btn.url = form.button_url.trim()
      if (form.button_type === "PHONE_NUMBER")
        btn.phone_number = form.button_phone.trim()
      payload.buttons = [btn]
    }

    setSaving(true)
    axios
      .post(`${companyBaseUrl()}/whatsapp/meta/templates`, payload, {
        headers: authHeaders(),
      })
      .then(res => {
        setSaving(false)
        if (res.data.status) {
          toast.success(
            res.data.message ||
              "Template submitted to Meta for review.",
            { position: "top-center", autoClose: 6000 }
          )
          setCreateOpen(false)
          fetchTemplates()
        } else {
          toast.error(res.data.message || "Create failed.", {
            position: "top-center",
            autoClose: 8000,
          })
        }
      })
      .catch(err => {
        setSaving(false)
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.data?.error?.message ||
            "Create failed.",
          { position: "top-center", autoClose: 10000 }
        )
      })
  }

  const deleteTemplate = tpl => {
    if (
      !window.confirm(
        `Delete template "${tpl.name}" from Meta? This cannot be undone.`
      )
    ) {
      return
    }
    setDeletingId(tpl.id)
    axios
      .delete(`${companyBaseUrl()}/whatsapp/meta/templates/${tpl.id}`, {
        headers: authHeaders(),
      })
      .then(res => {
        setDeletingId(null)
        if (res.data.status) {
          toast.success(res.data.message || "Template deleted.", {
            position: "top-center",
            autoClose: 4000,
          })
          fetchTemplates()
        } else {
          toast.error(res.data.message || "Delete failed.", {
            position: "top-center",
            autoClose: 7000,
          })
        }
      })
      .catch(err => {
        setDeletingId(null)
        toast.error(err?.response?.data?.message || "Delete failed.", {
          position: "top-center",
          autoClose: 8000,
        })
      })
  }

  if (connected === false) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="text-center py-5 px-4">
          <h5 className="mb-2">WhatsApp Business not connected</h5>
          <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 420 }}>
            Connect your WhatsApp Business account to create and manage message
            templates.
          </p>
          <Link to="/connect-whatsapp-meta" className="btn btn-success">
            Go to connection settings
          </Link>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="meta-wa-templates">
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h4 className="mb-1">Message templates</h4>
              <p className="text-muted mb-0 small">
                Create templates, track Meta review status, and sync your
                catalog.
                {metaInfo?.display_phone_number ? (
                  <>
                    {" "}
                    · Sending from{" "}
                    <strong>{metaInfo.display_phone_number}</strong>
                  </>
                ) : null}
                {Number(stats.pending || 0) > 0 ? (
                  <span className="text-warning ms-1">
                    · Auto-refreshing while reviews are pending
                  </span>
                ) : null}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Link
                to="/connect-whatsapp-meta"
                className="btn btn-outline-secondary"
              >
                Connection
              </Link>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={syncTemplates}
                disabled={syncing}
              >
                {syncing ? "Syncing…" : "Sync from Meta"}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={openCreate}
              >
                Create template
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Row className="mb-3 g-3">
        {[
          { label: "Total", value: stats.total, color: "#405189" },
          { label: "Approved", value: stats.approved, color: "#0ab39c" },
          { label: "Pending", value: stats.pending, color: "#f7b84b" },
          { label: "Rejected", value: stats.rejected, color: "#f06548" },
        ].map(item => (
          <Col md={3} sm={6} key={item.label}>
            <Card className="border-0 shadow-sm h-100 mb-0">
              <CardBody className="py-3">
                <div className="text-muted small text-uppercase">{item.label}</div>
                <div
                  className="fs-3 fw-semibold"
                  style={{ color: item.color }}
                >
                  {item.value ?? 0}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <input
                type="search"
                className="form-control"
                placeholder="Search templates…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") fetchTemplates()
                }}
              />
            </Col>
            <Col md={3}>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All statuses</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="PAUSED">Paused</option>
              </select>
            </Col>
            <Col md={3}>
              <select
                className="form-select"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                <option value="ALL">All categories</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Col>
            <Col md={2}>
              <button
                type="button"
                className="btn btn-light w-100"
                onClick={fetchTemplates}
                disabled={loading}
              >
                {loading ? "Loading…" : "Filter"}
              </button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2 mb-0">Loading templates…</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-5 border rounded bg-light">
              <h5 className="mb-2">No templates found</h5>
              <p className="text-muted mb-3 mx-auto" style={{ maxWidth: 420 }}>
                Create a new template for Meta review, or sync existing templates
                from your WhatsApp Business account.
              </p>
              <button
                type="button"
                className="btn btn-success me-2"
                onClick={openCreate}
              >
                Create template
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={syncTemplates}
                disabled={syncing}
              >
                Sync from Meta
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Template</th>
                    <th>Language</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Preview</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(t => (
                    <tr key={t.id}>
                      <td>
                        <div className="fw-semibold">{t.name}</div>
                        <small className="text-muted font-monospace">
                          {t.meta_template_id || "—"}
                        </small>
                      </td>
                      <td>{t.language || "—"}</td>
                      <td>
                        <StatusPill tone={categoryTone(t.category)}>
                          {t.category || "—"}
                        </StatusPill>
                      </td>
                      <td>
                        <StatusPill tone={statusTone(t.status)}>
                          {t.status || "UNKNOWN"}
                        </StatusPill>
                      </td>
                      <td
                        className="small text-muted"
                        style={{ maxWidth: 260 }}
                      >
                        {t.body_preview
                          ? t.body_preview.length > 100
                            ? `${t.body_preview.slice(0, 100)}…`
                            : t.body_preview
                          : "—"}
                      </td>
                      <td className="small text-muted">
                        {t.synced_at
                          ? new Date(t.synced_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-light me-1"
                          onClick={() => setPreviewTpl(t)}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={deletingId === t.id}
                          onClick={() => deleteTemplate(t)}
                        >
                          {deletingId === t.id ? "…" : "Delete"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create modal */}
      <Modal
        isOpen={createOpen}
        toggle={() => !saving && setCreateOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => !saving && setCreateOpen(false)}>
          Create message template
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Templates are submitted to Meta for review. Most start as{" "}
            <strong>Pending</strong> and become available once{" "}
            <strong>Approved</strong>. Use lowercase letters, numbers, and
            underscores in the template name.
          </p>
          <Row className="g-3">
            <Col md={6}>
              <label className="form-label">Template name *</label>
              <input
                className="form-control"
                value={form.name}
                onChange={e =>
                  setForm(f => ({
                    ...f,
                    name: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, "_"),
                  }))
                }
                placeholder="welcome_offer"
              />
            </Col>
            <Col md={3}>
              <label className="form-label">Language *</label>
              <select
                className="form-select"
                value={form.language}
                onChange={e =>
                  setForm(f => ({ ...f, language: e.target.value }))
                }
              >
                {LANGUAGES.map(l => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Col>
            <Col md={3}>
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                value={form.category}
                onChange={e =>
                  setForm(f => ({ ...f, category: e.target.value }))
                }
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Col>
            <Col md={12}>
              <label className="form-label">Header type</label>
              <select
                className="form-select mb-2"
                value={form.header_format}
                onChange={e =>
                  setForm(f => ({ ...f, header_format: e.target.value }))
                }
              >
                <option value="NONE">No header</option>
                <option value="TEXT">Text</option>
                <option value="IMAGE">Image attachment</option>
                <option value="VIDEO">Video attachment</option>
                <option value="DOCUMENT">Document attachment</option>
              </select>
              {form.header_format === "TEXT" ? (
                <input
                  className="form-control"
                  maxLength={60}
                  value={form.header_text}
                  onChange={e =>
                    setForm(f => ({ ...f, header_text: e.target.value }))
                  }
                  placeholder="Welcome to KlikCRM"
                />
              ) : null}
              {["IMAGE", "VIDEO", "DOCUMENT"].includes(form.header_format) ? (
                <>
                  <input
                    className="form-control"
                    value={form.header_example_url}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        header_example_url: e.target.value,
                      }))
                    }
                    placeholder="https://example.com/sample.jpg"
                  />
                  <small className="text-muted">
                    Public HTTPS sample file for Meta review (jpg/png, mp4, or
                    pdf). When sending later, pass the real file URL as
                    header_link.
                  </small>
                </>
              ) : null}
            </Col>
            <Col md={12}>
              <label className="form-label">Body *</label>
              <textarea
                className="form-control"
                rows={4}
                maxLength={1024}
                value={form.body_text}
                onChange={e =>
                  setForm(f => ({ ...f, body_text: e.target.value }))
                }
                placeholder="Hello {{1}}, your order {{2}} is confirmed."
              />
              <small className="text-muted">
                Use variables like <code>{"{{1}}"}</code>, <code>{"{{2}}"}</code>
              </small>
            </Col>
            {bodyVarIndexes.map((n, i) => (
              <Col md={6} key={n}>
                <label className="form-label">
                  Sample value for {"{{"}
                  {n}
                  {"}}"} *
                </label>
                <input
                  className="form-control"
                  value={form.body_examples[i] || ""}
                  onChange={e => {
                    const next = [...form.body_examples]
                    next[i] = e.target.value
                    setForm(f => ({ ...f, body_examples: next }))
                  }}
                  placeholder={n === 1 ? "Alex" : "ORD-1001"}
                />
              </Col>
            ))}
            <Col md={12}>
              <label className="form-label">Footer (optional)</label>
              <input
                className="form-control"
                maxLength={60}
                value={form.footer_text}
                onChange={e =>
                  setForm(f => ({ ...f, footer_text: e.target.value }))
                }
                placeholder="Thank you for choosing us"
              />
            </Col>
            <Col md={12}>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="btnEnabled"
                  checked={form.button_enabled}
                  onChange={e =>
                    setForm(f => ({
                      ...f,
                      button_enabled: e.target.checked,
                    }))
                  }
                />
                <label className="form-check-label" htmlFor="btnEnabled">
                  Add a button (optional)
                </label>
              </div>
              {form.button_enabled ? (
                <Row className="g-2">
                  <Col md={4}>
                    <select
                      className="form-select"
                      value={form.button_type}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          button_type: e.target.value,
                        }))
                      }
                    >
                      <option value="QUICK_REPLY">Quick reply</option>
                      <option value="URL">URL</option>
                      <option value="PHONE_NUMBER">Phone number</option>
                    </select>
                  </Col>
                  <Col md={4}>
                    <input
                      className="form-control"
                      placeholder="Button text"
                      maxLength={25}
                      value={form.button_text}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          button_text: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col md={4}>
                    {form.button_type === "URL" ? (
                      <input
                        className="form-control"
                        placeholder="https://…"
                        value={form.button_url}
                        onChange={e =>
                          setForm(f => ({
                            ...f,
                            button_url: e.target.value,
                          }))
                        }
                      />
                    ) : form.button_type === "PHONE_NUMBER" ? (
                      <input
                        className="form-control"
                        placeholder="9198xxxxxxxx"
                        value={form.button_phone}
                        onChange={e =>
                          setForm(f => ({
                            ...f,
                            button_phone: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      <input
                        className="form-control"
                        disabled
                        placeholder="Quick reply — no URL needed"
                      />
                    )}
                  </Col>
                </Row>
              ) : null}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            disabled={saving}
            onClick={() => setCreateOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={saving}
            onClick={submitCreate}
          >
            {saving ? "Submitting…" : "Submit for review"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Preview modal */}
      <Modal
        isOpen={!!previewTpl}
        toggle={() => setPreviewTpl(null)}
        centered
      >
        <ModalHeader toggle={() => setPreviewTpl(null)}>
          {previewTpl?.name}
        </ModalHeader>
        <ModalBody>
          {previewTpl ? (
            <>
              <div className="mb-2 d-flex flex-wrap gap-2 align-items-center">
                <StatusPill tone={statusTone(previewTpl.status)}>
                  {previewTpl.status || "UNKNOWN"}
                </StatusPill>
                <StatusPill tone={categoryTone(previewTpl.category)}>
                  {previewTpl.category || "—"}
                </StatusPill>
                <span className="small text-muted">{previewTpl.language}</span>
              </div>
              <div
                className="p-3 rounded"
                style={{
                  background: "#e7ffdb",
                  border: "1px solid #c6e9b0",
                  whiteSpace: "pre-wrap",
                }}
              >
                {(previewTpl.components || []).map((c, idx) => {
                  const type = String(c.type || "").toUpperCase()
                  if (type === "HEADER") {
                    return (
                      <div key={idx} className="fw-bold mb-1">
                        {c.text}
                      </div>
                    )
                  }
                  if (type === "BODY") {
                    return (
                      <div key={idx} className="mb-1">
                        {c.text}
                      </div>
                    )
                  }
                  if (type === "FOOTER") {
                    return (
                      <div key={idx} className="small text-muted mt-2">
                        {c.text}
                      </div>
                    )
                  }
                  if (type === "BUTTONS") {
                    return (
                      <div key={idx} className="mt-2">
                        {(c.buttons || []).map((b, bi) => (
                          <div
                            key={bi}
                            className="btn btn-sm btn-light w-100 mb-1"
                          >
                            {b.text}{" "}
                            <span className="text-muted small">
                              ({b.type})
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                  }
                  return null
                })}
                {!previewTpl.components?.length && (
                  <div>{previewTpl.body_preview || "No preview"}</div>
                )}
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setPreviewTpl(null)}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default MetaWhatsAppTemplatesManager
